import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const RESET_DELAY_MS = 2000;

interface CopyOptions {
  /** Chave que identifica o campo copiado (permite múltiplos botões independentes). */
  key?: string;
  /** Mensagem de sucesso exibida via toast. Passe `null` para não exibir toast. */
  successMessage?: string | null;
}

/**
 * Hook de cópia para a área de transferência com estado por campo.
 *
 * Diferente de um único booleano compartilhado, `copiedKey` identifica qual
 * campo foi copiado por último, evitando que o feedback (✓) "vaze" entre botões.
 */
export function useCopyToClipboard() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const copy = useCallback(
    async (value: string | number | null | undefined, options: CopyOptions = {}) => {
      if (value === null || value === undefined || value === "") return;

      const { key = "default", successMessage = "Copiado" } = options;

      try {
        await navigator.clipboard.writeText(String(value));
        setCopiedKey(key);
        if (successMessage) toast.success(successMessage);

        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setCopiedKey(null), RESET_DELAY_MS);
      } catch {
        toast.error("Não foi possível copiar");
      }
    },
    [],
  );

  return { copiedKey, copy };
}
